import { Box, FormControlLabel, Stack, Switch, useTheme } from "@mui/material";
import { Licensing_V1 } from "@rw1/auto-generated-clients";
import { FC, useState } from "react";
import { ConfirmationDialog } from "../../../components";
import { useLocalization } from "../../../hooks/useLocalization";
import { now } from "../../../shared/utils/date";
import { CalendarPopover } from "../../company/components/CalendarPopover";
import { LicenseWithExperienceName } from "../hooks/useLicenseById";
import { useUpdateLicense } from "../hooks/useUpdateLicense";

interface EditExpirationDateDialogProps {
  open: boolean;
  onClose: () => void;
  license: LicenseWithExperienceName;
}

export const EditExpirationDateDialog: FC<EditExpirationDateDialogProps> = ({
  open,
  onClose,
  license,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [checked, setChecked] = useState(!license.expiresAt);
  const [date, setDate] = useState<Date | null>(license.expiresAt ?? null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const updateLicenseMutation = useUpdateLicense();

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
  };

  const handleDateApply = (newDate: Date | null) => {
    setDate(newDate);
  };

  const handleCancel = () => {
    setDate(license.expiresAt ?? null);
    setChecked(!license.expiresAt);
    onClose();
    updateLicenseMutation.reset();
  };

  const onUpdateExpirationDate = () => {
    const newExpirationDate = checked || !date ? undefined : date;

    const updatedLicense = new Licensing_V1.LicenseBase({
      ...license,
      expiresAt: newExpirationDate,
    });

    updateLicenseMutation.mutate({
      licenseId: license.licenseId,
      licenseData: updatedLicense,
      onSuccess: onClose,
    });
  };

  const isApplyDisabled =
    (!checked && !date) || updateLicenseMutation.isPending;

  return (
    <ConfirmationDialog.Root
      fullScreenOnMobile
      maxWidth="xs"
      onClose={handleCancel}
      open={open}
    >
      <ConfirmationDialog.Title showIcon={true}>
        {t("licenses.changeExpirationDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content error={updateLicenseMutation.error}>
        <Stack spacing={2}>
          <ConfirmationDialog.Text sx={{ pb: 2 }}>
            {t("licenses.changeExpirationDialog.description")}
          </ConfirmationDialog.Text>
          <FormControlLabel
            control={<Switch checked={checked} onChange={handleSwitchChange} />}
            label={t("licenses.changeExpirationDialog.unlimited")}
            sx={{
              marginLeft: 0,
              width: "fit-content",
              "& .MuiFormControlLabel-label": {
                fontSize: 12,
                fontWeight: 500,
                color: theme.colors.text[checked ? 7 : 0],
                ml: 1,
              },
            }}
          />
          <Box
            sx={{
              border: `1px solid ${theme.colors.secondary[1]}`,
              borderRadius: "16px",
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              pb: 1,
            }}
          >
            <CalendarPopover
              alwaysVisible={true}
              anchorEl={null}
              disabled={checked}
              enableFuture={true}
              hideButtons={true}
              minDate={now()}
              noDefaultValue={!date}
              onApply={handleDateApply}
              onClose={handleCalendarClose}
              onTempChange={handleDateApply}
              open={isCalendarOpen}
              value={date}
            />
          </Box>
        </Stack>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("licenses.changeExpirationDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isApplyDisabled}
          isLoading={updateLicenseMutation.isPending}
          loadingText={t("licenses.changeExpirationDialog.applying")}
          onClick={onUpdateExpirationDate}
          variant="contained"
        >
          {t("licenses.changeExpirationDialog.apply")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
